import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AllergenStatus } from '../types';
import { ProductAllergenEntry, emptyAllergenRow } from '../types';

interface AllergensTabProps {
  allergens: ProductAllergenEntry[];
  onChange: (allergens: ProductAllergenEntry[]) => void;
  disabled?: boolean;
}

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const AllergensTab: FC<AllergensTabProps> = ({ allergens, onChange, disabled }) => {
  const { t } = useTranslation('productManagement');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const updateRow = (index: number, patch: Partial<ProductAllergenEntry>) => {
    const next = allergens.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    // Row identity is positional (key={index}); reset expanded state so a delete
    // can't leave a later row's expanded panel misattributed to its new position.
    setExpandedRows({});
    onChange(allergens.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...allergens, emptyAllergenRow()]);
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="textLG"
        component="div"
        sx={{ mb: 4, fontWeight: 600 }}>
        {t('allergens', 'Allergens')}
      </Typography>

      {allergens.length === 0 && (
        <Typography variant="textSM"
          component="div"
          color="text.secondary"
          sx={{ mb: 3 }}>
          {t('noAllergensCaptured', 'No allergens captured yet.')}
        </Typography>
      )}

      {allergens.map((row, index) => {
        const isExpanded = Boolean(expandedRows[index]);
        return (
          <Box key={index}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, pt: '12px', pr: 3, pb: 0, pl: 3, mb: 3 }}>
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                xs={12}
                sm={4}>
                <TextField
                  label={t('allergenCode', 'Code')}
                  value={row.allergenCode}
                  onChange={(e) => updateRow(index, { allergenCode: e.target.value })}
                  size="small"
                  disabled={disabled}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={12}
                sm={4}>
                <TextField
                  label={t('allergenName', 'Allergen Name')}
                  value={row.allergenName ?? ''}
                  onChange={(e) => updateRow(index, { allergenName: e.target.value })}
                  size="small"
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              <Grid item
                xs={8}
                sm={3}>
                <Select
                  value={row.allergenStatus ?? AllergenStatus.Unknown}
                  onChange={(e: SelectChangeEvent) => updateRow(index, { allergenStatus: e.target.value })}
                  size="small"
                  disabled={disabled}
                  fullWidth
                  aria-label={t('status', 'Status')}
                >
                  <MenuItem value={AllergenStatus.Contains}>{t('allergenStatusContains', 'Contains')}</MenuItem>
                  <MenuItem value={AllergenStatus.MayContain}>{t('allergenStatusMayContain', 'May Contain')}</MenuItem>
                  <MenuItem value={AllergenStatus.NotPresent}>{t('allergenStatusNotPresent', 'Not Present')}</MenuItem>
                  <MenuItem value={AllergenStatus.Unknown}>{t('allergenStatusUnknown', 'Unknown')}</MenuItem>
                </Select>
              </Grid>
              <Grid item
                xs={4}
                sm={1}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => toggleExpanded(index)}
                  aria-label={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  aria-label={t('remove', 'Remove')}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Grid container
                spacing={3}
                sx={{ mt: 0, pt: 0, pb: 0, pl: '12px', pr: '12px' }}>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenVariantCode', 'Variant Code')}
                    value={row.allergenVariantCode ?? ''}
                    onChange={(e) => updateRow(index, { allergenVariantCode: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenVariantName', 'Variant Name')}
                    value={row.allergenVariantName ?? ''}
                    onChange={(e) => updateRow(index, { allergenVariantName: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('allergenDerivativeCode', 'Derivative Code')}
                    value={row.allergenDerivativeCode ?? ''}
                    onChange={(e) => updateRow(index, { allergenDerivativeCode: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('concentration', 'Concentration')}
                    type="number"
                    value={row.concentration ?? ''}
                    onChange={(e) => updateRow(index, { concentration: e.target.value === '' ? undefined : Number(e.target.value) })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('concentrationUom', 'Concentration Unit')}
                    value={row.concentrationUom ?? ''}
                    onChange={(e) => updateRow(index, { concentrationUom: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('sourceType', 'Source Type')}
                    value={row.sourceType ?? ''}
                    onChange={(e) => updateRow(index, { sourceType: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('sourceReference', 'Source Reference')}
                    value={row.sourceReference ?? ''}
                    onChange={(e) => updateRow(index, { sourceReference: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('supplierConfirmed', 'Supplier Confirmed')}
                    value={row.supplierConfirmed ?? ''}
                    onChange={(e) => updateRow(index, { supplierConfirmed: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveFrom', 'Effective From')}
                    value={toDateValue(row.effectiveFrom)}
                    onChange={(newValue) => updateRow(index, { effectiveFrom: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveTo', 'Effective To')}
                    value={toDateValue(row.effectiveTo)}
                    onChange={(newValue) => updateRow(index, { effectiveTo: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('notes', 'Notes')}
                    value={row.notes ?? ''}
                    onChange={(e) => updateRow(index, { notes: e.target.value })}
                    size="small"
                    disabled={disabled}
                    fullWidth
                    multiline
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}

      <Button startIcon={<AddIcon />}
        onClick={addRow}
        disabled={disabled}
        sx={{ mt: 2 }}>
        {t('addAllergen', 'Add allergen')}
      </Button>
    </Box>
  );
};

export default AllergensTab;
